import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import Stack from "@mui/material/Stack";
import Zoom from "@mui/material/Zoom";
import { useMediaQuery } from "@mui/material";
import { Theme } from "@mui/material/styles";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { AcademyIcon, CloseIcon, OpenIcon, PrivacyIcon, TermsAndPrivacyIcon } from "@common/utils/icons/icons";
import NavigationRoutes from "@common/enums/query-params/sphere-navigation-routes";
import NavigationButton from "../ui/NavigationButton";
import { isRouteActive } from "utils/route-matching";

const PublicNavigationBar: React.FC<{ isNavOpen: boolean; onNavChangeHandler: () => void }> = ({
  isNavOpen,
  onNavChangeHandler,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [isToggleSideBarButtonVisible, setIsToggleSideBarButtonVisible] = useState(false);

  const isPageOpen = (route: NavigationRoutes) => isRouteActive(pathname, route);

  const navigateToRouteHandler = (route: NavigationRoutes) => {
    navigate(route);
  };

  const newFeaturesClickHandler = () => {
    window.open(t("link-new-features"), "_blank", "noopener");
  };

  const knowledgeHubClickHandler = () => {
    window.open(t("link-knowledge-hub"), "_blank", "noopener");
  };

  const mouseOverNavHandler = () => {
    setIsToggleSideBarButtonVisible(true);
  };

  const mouseOffNavHandler = () => {
    setIsToggleSideBarButtonVisible(false);
  };

  const toggleSideBarClickHandler = () => {
    setIsToggleSideBarButtonVisible(false);
    onNavChangeHandler();
  };

  const isExpanded = isNavOpen && !matchesSmDown;

  return (
    <Box
      position="relative"
      minWidth="min-content"
      sx={{ px: 1, pb: { xs: 0, sm: 1 } }}
      onMouseEnter={mouseOverNavHandler}
      onMouseLeave={mouseOffNavHandler}
      data-testid="public-nav-bar"
    >
      {!matchesSmDown && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(4.5),
            right: theme.spacing(-2),
            borderRadius: "50%",
            zIndex: theme.zIndex.fab,
          })}
        >
          <Zoom in={isToggleSideBarButtonVisible} timeout={100} unmountOnExit>
            <Fab
              onClick={toggleSideBarClickHandler}
              size="small"
              data-testid="toggle-public-nav-bar"
              sx={(theme) => ({
                boxShadow: 2,
                background: theme.palette.background.default,
                ":hover": { backgroundColor: theme.palette.action.hover },
              })}
            >
              {isNavOpen ? <CloseIcon /> : <OpenIcon />}
            </Fab>
          </Zoom>
        </Box>
      )}
      <Stack
        data-testid="public-nav-bar-container"
        sx={(theme) => ({
          transition: "all 0.4s ease-in-out",
          overflowX: "hidden",
          width: { xs: '100%', sm: isExpanded ? theme.spacing(32) : theme.spacing(7) },
          height: matchesSmDown ? "auto" : "100%",
        })}
        spacing={1}
      >
        <NavigationButton
          isCurrentPage={isPageOpen(NavigationRoutes.Legal)}
          isNavOpen={isExpanded}
          title={t("button-legal")}
          onClick={() => navigateToRouteHandler(NavigationRoutes.Legal)}
          icon={<TermsAndPrivacyIcon />}
        />
        <NavigationButton
          isCurrentPage={isPageOpen(NavigationRoutes.AccountData)}
          isNavOpen={isExpanded}
          title={t("button-account-data")}
          onClick={() => navigateToRouteHandler(NavigationRoutes.AccountData)}
          icon={<PrivacyIcon />}
        />
        <NavigationButton
          isNavOpen={isExpanded}
          title={t("info-new-features")}
          onClick={newFeaturesClickHandler}
          icon={<TipsAndUpdatesRoundedIcon />}
        />
        <NavigationButton
          isNavOpen={isExpanded}
          title={t("info-kemaro-hubspot")}
          onClick={knowledgeHubClickHandler}
          icon={<AcademyIcon />}
        />
      </Stack>
    </Box>
  );
};

export default PublicNavigationBar;
